import React, { useEffect, useState } from "react";
import "./FinancePage.css";
import { getBillingInvoice } from "../../api/paymentsApi";
import RecordPaymentModal from "./RecordPaymentModal";

const money = (value) => {
  // No value is "—", never a fabricated $0.00.
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_CLASS = {
  paid: "success",
  issued: "warn",
  partially_paid: "warn",
  overdue: "danger",
  draft: "",
  void: "",
};

const STATUS_LABEL = {
  paid: "Paid",
  issued: "Issued",
  partially_paid: "Partial",
  overdue: "Overdue",
  draft: "Draft",
  void: "Void",
};

const SCHEDULE_STATUS = {
  paid: "success",
  pending: "warn",
  overdue: "danger",
  void: "",
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function InvoiceDetailModal({ invoiceId, onClose, onPaymentRecorded }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPayment, setShowPayment] = useState(false);

  const loadInvoice = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getBillingInvoice(invoiceId);
      setInvoice(res?.data);
    } catch (e) {
      setError(e?.message || "Unable to load invoice.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (invoiceId) loadInvoice();
  }, [invoiceId]);

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    loadInvoice();
    if (onPaymentRecorded) onPaymentRecorded();
  };

  if (!invoiceId) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} onClick={onClose} />
      <div style={{
        position: "relative", width: "90%", maxWidth: 720, maxHeight: "85vh", overflow: "auto",
        borderRadius: 14, border: "1px solid var(--border)",
        background: "var(--panel-glass, var(--glass-bg, #1a1a2e))",  padding: 24,
        boxShadow: "var(--shadow-overlay)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>Invoice Detail</h2>
          <button className="finance-btn secondary" onClick={onClose}>Close</button>
        </div>

        {loading && <div className="finance-muted" style={{ padding: 20 }}>Loading...</div>}
        {error && <div className="finance-muted" style={{ padding: 20, color: "var(--danger)" }}>{error}</div>}

        {invoice && !loading && (
          <>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{invoice.invoice_number || "—"}</div>
                <div className="finance-muted">{invoice.customer_name || "No customer"}</div>
                {invoice.quote && <div className="finance-muted">Quote: {String(invoice.quote).slice(0, 8)}...</div>}
              </div>
              <span className={`finance-pill ${STATUS_CLASS[invoice.status] || ""}`} style={{ fontSize: 14, padding: "4px 12px" }}>
                {STATUS_LABEL[invoice.status] || invoice.status}
              </span>
            </div>

            {/* Stats */}
            <section className="finance-stat-grid" style={{ marginBottom: 16 }}>
              <div className="finance-stat">
                <div className="label">Total</div>
                <div className="value" style={{ fontSize: 18 }}>{money(invoice.total)}</div>
              </div>
              <div className="finance-stat">
                <div className="label">Paid</div>
                <div className="value" style={{ fontSize: 18 }}>{money(invoice.amount_paid)}</div>
              </div>
              <div className="finance-stat">
                <div className="label">Balance Due</div>
                <div className="value" style={{ fontSize: 18, color: Number(invoice.balance_due || 0) > 0 ? "var(--color-danger)" : "var(--color-success)" }}>
                  {money(invoice.balance_due)}
                </div>
              </div>
            </section>

            {/* Record Payment button */}
            {invoice.status !== "paid" && invoice.status !== "void" && (
              <div style={{ marginBottom: 16 }}>
                <button className="finance-btn" onClick={() => setShowPayment(true)}>
                  Record Payment
                </button>
              </div>
            )}

            {/* Line Items */}
            {invoice.line_items?.length > 0 && (
              <section className="finance-card" style={{ marginBottom: 12 }}>
                <div className="finance-card-head"><h3>Line Items</h3></div>
                <div className="finance-table" style={{ "--finance-columns": "2fr 0.6fr 0.8fr 0.8fr" }}>
                  <div className="finance-row head">
                    <div>Description</div>
                    <div>Qty</div>
                    <div>Unit Price</div>
                    <div>Total</div>
                  </div>
                  {invoice.line_items.map((li, idx) => (
                    <div key={li.id || idx} className="finance-row">
                      <div>{li.description || "—"}</div>
                      <div>{li.quantity}</div>
                      <div>{money(li.unit_price)}</div>
                      <div>{money(li.total)}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Payment Schedule */}
            {invoice.schedule?.length > 0 && (
              <section className="finance-card" style={{ marginBottom: 12 }}>
                <div className="finance-card-head"><h3>Payment Schedule</h3></div>
                <div className="finance-table" style={{ "--finance-columns": "1.5fr 0.8fr 0.8fr 0.7fr" }}>
                  <div className="finance-row head">
                    <div>Milestone</div>
                    <div>Amount</div>
                    <div>Due</div>
                    <div>Status</div>
                  </div>
                  {invoice.schedule.map((s, idx) => (
                    <div key={s.id || idx} className="finance-row">
                      <div>{s.label || "—"}</div>
                      <div>{money(s.amount)}</div>
                      <div>{formatDate(s.due_at)}</div>
                      <div>
                        <span className={`finance-pill ${SCHEDULE_STATUS[s.status] || ""}`}>
                          {s.status === "paid" ? "Paid" : s.status === "pending" ? "Pending" : s.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Payments */}
            {invoice.payments?.length > 0 && (
              <section className="finance-card">
                <div className="finance-card-head"><h3>Payments</h3></div>
                <div className="finance-table" style={{ "--finance-columns": "0.8fr 0.8fr 0.8fr 1fr 0.8fr" }}>
                  <div className="finance-row head">
                    <div>Amount</div>
                    <div>Method</div>
                    <div>Category</div>
                    <div>Reference</div>
                    <div>Date</div>
                  </div>
                  {invoice.payments.map((p, idx) => (
                    <div key={p.id || idx} className="finance-row">
                      <div>{money(p.amount)}</div>
                      <div style={{ textTransform: "capitalize" }}>{p.payment_method_type || "—"}</div>
                      <div style={{ textTransform: "capitalize" }}>{p.payment_category || "—"}</div>
                      <div className="mono">{p.external_reference || p.source || "—"}</div>
                      <div>{formatDate(p.received_at || p.created_at)}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Metadata */}
            <div className="finance-muted" style={{ marginTop: 12, fontSize: 11 }}>
              Issued: {formatDate(invoice.issued_at)} | Terms: {invoice.payment_terms || "—"} | Created: {formatDate(invoice.created_at)}
            </div>
          </>
        )}

        {showPayment && invoice && (
          <RecordPaymentModal
            invoice={invoice}
            onClose={() => setShowPayment(false)}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </div>
    </div>
  );
}
